/**
 * Storefront event helpers.
 *
 * Thin fetch wrappers that post analytics events to the backend via the
 * Shopify App Proxy. Never throws — every call is best-effort. The widget
 * must stay usable even when the analytics endpoint is unreachable.
 */

import { getDeviceHint, getOrCreateSessionId } from "../session";

const PROXY_BASE = "/apps/primestyle";
const TAG = "[primestyle-events]";

function getShopDomain(): string | null {
	if (typeof window === "undefined") return null;
	try {
		const w = window as unknown as { Shopify?: { shop?: string } };
		if (w.Shopify?.shop) return w.Shopify.shop;
	} catch {
		/* noop */
	}
	return typeof window.location !== "undefined" ? window.location.hostname : null;
}

async function post(
	path: string,
	body: Record<string, unknown>,
	options: { throwOnError?: boolean } = {},
): Promise<void> {
	const shopDomain = getShopDomain();
	if (!shopDomain) return;
	try {
		const res = await fetch(`${PROXY_BASE}${path}`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ shopDomain, ...body }),
			keepalive: true,
		});
		if (!res.ok && options.throwOnError) {
			let message = `Storefront event failed (${res.status})`;
			try {
				const data = (await res.json()) as { error?: unknown; message?: unknown };
				if (typeof data?.error === "string") message = data.error;
				else if (typeof data?.message === "string") message = data.message;
			} catch {
				/* noop */
			}
			throw new Error(message);
		}
	} catch (err) {
		if (options.throwOnError) throw err;
		console.warn(TAG, `post ${path} failed`, err);
	}
}

export function logProductView(input: {
	productId?: string;
	productTitle?: string;
}): void {
	void post("/events/product-view", {
		sessionId: getOrCreateSessionId(),
		productId: input.productId,
		productTitle: input.productTitle,
	});
}

export function logSizeAccepted(input: {
	productId?: string;
	productTitle?: string;
	size?: string;
	recommendedSize?: string;
}): void {
	void post("/events/size-accepted", {
		sessionId: getOrCreateSessionId(),
		productId: input.productId,
		productTitle: input.productTitle,
		size: input.size,
		recommendedSize: input.recommendedSize,
	});
}

export function logSizeShown(input: {
	productId?: string;
	productTitle?: string;
	recommendedSize?: string;
	fromCache?: boolean;
}): void {
	void post("/events/size-shown", {
		sessionId: getOrCreateSessionId(),
		productId: input.productId,
		productTitle: input.productTitle,
		recommendedSize: input.recommendedSize,
		fromCache: input.fromCache,
	});
}

export function logTryOnFeedback(input: {
	jobId?: string;
	historyEntryId?: string;
	rating?: number;
	note?: string;
	productId?: string;
	productTitle?: string;
	productUrl?: string;
	recommendedSize?: string;
	profileLoggedIn?: boolean;
	profileId?: string;
	profileName?: string;
	profileAccessToken?: string;
}): Promise<void> {
	return post("/events/tryon-feedback", {
		sessionId: getOrCreateSessionId(),
		deviceHint: getDeviceHint(),
		jobId: input.jobId,
		historyEntryId: input.historyEntryId,
		rating: input.rating,
		note: input.note,
		productId: input.productId,
		productTitle: input.productTitle,
		productUrl: input.productUrl,
		recommendedSize: input.recommendedSize,
		profileLoggedIn: input.profileLoggedIn,
		profileId: input.profileId,
		profileName: input.profileName,
		profileAccessToken: input.profileAccessToken,
	}, { throwOnError: true });
}

export function logProfileEvent(input: {
	eventType: "SDK_PROFILE_CREATED" | "SDK_PROFILE_UPDATED" | "SDK_PROFILE_SELECTED";
	productId?: string;
	productTitle?: string;
	productUrl?: string;
	profileLoggedIn?: boolean;
	profileId?: string;
	profileName?: string;
	profileOrigin?: string;
	metadata?: Record<string, unknown>;
}): void {
	void post("/events/profile", {
		sessionId: getOrCreateSessionId(),
		deviceHint: getDeviceHint(),
		eventType: input.eventType,
		productId: input.productId,
		productTitle: input.productTitle,
		productUrl: input.productUrl,
		profileLoggedIn: input.profileLoggedIn,
		profileId: input.profileId,
		profileName: input.profileName,
		profileOrigin: input.profileOrigin,
		metadata: input.metadata,
	});
}
